import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can, P } from "@/lib/perms";
import { InstallProvider } from "@/context/InstallContext";
import { Loader } from "@/components/EmptyState";
import { InstallAppBanner } from "@/components/InstallAppBanner";

import Landing from "@/pages/Landing";
import BusinessLogin from "@/pages/BusinessLogin";
import Register from "@/pages/Register";
import Gate from "@/pages/Gate";
import { needsPlan } from "@/lib/subscription";
import Resubmit from "@/pages/Resubmit";
import Legal from "@/pages/Legal";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import DashboardLayout from "@/pages/app/DashboardLayout";
import Dashboard from "@/pages/app/Dashboard";
import Products from "@/pages/app/Products";
import Organization from "@/pages/app/Organization";
import OrgSetup from "@/pages/app/OrgSetup";
import Partners from "@/pages/app/Partners";
import Orders from "@/pages/app/Orders";
import Reports from "@/pages/app/Reports";
import Inventory from "@/pages/app/Inventory";
import Notifications from "@/pages/app/Notifications";
import Settings from "@/pages/app/Settings";
import Profile from "@/pages/app/Profile";

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  return children;
}

function RedirectIfAuthed({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/gate"} replace />;
  return children;
}

function RequirePerm({ perms, children }) {
  const { user } = useAuth();
  if (!can(user, ...perms)) {
    return <p className="text-stone-500" data-testid="no-access-message">You don't have access to this section. Ask your Super Admin for permission.</p>;
  }
  return children;
}

function RequireBusiness({ children }) {
  const { user, business, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  const active =
    business &&
    business.registration_status === "approved" &&
    business.account_active && !needsPlan(business);
  if (!active) return <Navigate to="/gate" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <InstallProvider>
      <BrowserRouter>
        <InstallAppBanner />
        <Routes>
          <Route path="/" element={<RedirectIfAuthed><Landing /></RedirectIfAuthed>} />
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<RedirectIfAuthed><BusinessLogin /></RedirectIfAuthed>} />
          <Route path="/register" element={<Register />} />
          <Route path="/subscribe" element={<Navigate to="/gate" replace />} />
          <Route path="/gate" element={<Gate />} />
          <Route path="/resubmit" element={<Resubmit />} />
          <Route path="/terms" element={<Legal />} />
          <Route path="/privacy" element={<Legal />} />
          <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/app" element={<RequireBusiness><DashboardLayout /></RequireBusiness>}>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<RequirePerm perms={P.catalog}><Products /></RequirePerm>} />
            <Route path="operators" element={<Navigate to="/app/organization" replace />} />
            <Route path="organization" element={<Organization />} />
            <Route path="setup" element={<OrgSetup />} />
            <Route path="partners" element={<Partners />} />
            <Route path="orders" element={<RequirePerm perms={P.orders}><Orders /></RequirePerm>} />
            <Route path="reports" element={<RequirePerm perms={P.reports}><Reports /></RequirePerm>} />
            <Route path="inventory" element={<RequirePerm perms={P.inventory}><Inventory /></RequirePerm>} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </InstallProvider>
    </div>
  );
}

export default App;
